import React from 'react';
import { Typography, Row, Col, Space, Button, Upload, message } from 'antd';
import { PlusOutlined, CameraOutlined, FileImageOutlined } from '@ant-design/icons';
import ToothSVG from './ToothSVG';

const { Title, Text } = Typography;

const TOOTH_NUMBERS = {
  adult: [
    [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28],
    [48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38]
  ]
};

const DentalChart = ({ onToothClick, selectedTooth, chartData, setChartData }) => {
  const toothNumbers = TOOTH_NUMBERS.adult;

  const handleXrayUpload = (info) => {
    if (info.file.status === 'done') {
      message.success('X-ray uploaded successfully');
    }
  };

  return (
    <div style={{ 
      padding: '24px', 
      backgroundColor: 'white', 
      borderRadius: '12px',
      boxShadow: '0 4px 16px rgba(0,0,0,0.06)'
    }}>
      <Title level={4} style={{ 
        textAlign: 'center', 
        marginBottom: '24px',
        color: '#262626'
      }}>
        Professional Dental Chart
      </Title>
      
      {/* Quadrant Labels */}
      <Row justify="space-between" style={{ marginBottom: '12px', padding: '0 40px' }}>
        <Col><Text type="secondary" style={{ fontSize: '12px', fontWeight: 'bold' }}>Upper Right (Q1)</Text></Col>
        <Col><Text type="secondary" style={{ fontSize: '12px', fontWeight: 'bold' }}>Upper Left (Q2)</Text></Col>
      </Row>
      
      {/* Upper teeth */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        marginBottom: '24px',
        padding: '16px',
        backgroundColor: '#fafafa',
        borderRadius: '12px',
        border: '2px solid #e8e8e8'
      }}>
        {toothNumbers[0].map(num => (
          <ToothSVG 
            key={num} 
            number={num} 
            isSelected={selectedTooth === num}
            onClick={onToothClick}
            isUpper={true}
            chartData={chartData}
          />
        ))}
      </div>
      
      {/* Center logo area */}
      <div style={{ 
        textAlign: 'center', 
        margin: '20px 0',
        display: 'flex',
        justifyContent: 'center',
        gap: '12px'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          backgroundColor: '#1890ff',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontWeight: 'bold'
        }}>
          🦷
        </div>
        <div style={{
          width: '40px',
          height: '40px',
          backgroundColor: '#1890ff',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontWeight: 'bold'
        }}>
          🦷
        </div>
      </div>
      
      {/* Lower teeth */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        marginBottom: '20px',
        padding: '16px',
        backgroundColor: '#fafafa',
        borderRadius: '12px',
        border: '2px solid #e8e8e8'
      }}>
        {toothNumbers[1].map(num => (
          <ToothSVG 
            key={num} 
            number={num} 
            isSelected={selectedTooth === num}
            onClick={onToothClick}
            isUpper={false}
            chartData={chartData}
          />
        ))}
      </div>
      
      {/* Quadrant Labels */}
      <Row justify="space-between" style={{ marginBottom: '20px', padding: '0 40px' }}>
        <Col><Text type="secondary" style={{ fontSize: '12px', fontWeight: 'bold' }}>Lower Right (Q4)</Text></Col>
        <Col><Text type="secondary" style={{ fontSize: '12px', fontWeight: 'bold' }}>Lower Left (Q3)</Text></Col>
      </Row>

      {/* Enhanced Legend */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        flexWrap: 'wrap', 
        gap: '16px',
        padding: '20px',
        backgroundColor: '#f0f2f5',
        borderRadius: '12px',
        marginTop: '20px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '12px', height: '12px', backgroundColor: '#fa8c16', borderRadius: '50%' }} />
          <Text style={{ fontSize: '12px', fontWeight: '500' }}>Decay</Text>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '12px', height: '12px', backgroundColor: '#52c41a', borderRadius: '50%' }} />
          <Text style={{ fontSize: '12px', fontWeight: '500' }}>Filled</Text>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '12px', height: '12px', backgroundColor: '#1890ff', borderRadius: '50%' }} />
          <Text style={{ fontSize: '12px', fontWeight: '500' }}>Crown</Text>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '12px', height: '12px', backgroundColor: '#722ed1', borderRadius: '50%' }} />
          <Text style={{ fontSize: '12px', fontWeight: '500' }}>Root Canal</Text>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '12px', height: '12px', backgroundColor: '#ff4d4f', borderRadius: '50%' }} />
          <Text style={{ fontSize: '12px', fontWeight: '500' }}>Extracted</Text>
        </div>
      </div>
      
      {/* Tooth numbering system info */}
      <div style={{ 
        marginTop: '16px',
        textAlign: 'center',
        padding: '12px',
        backgroundColor: '#f6f8fa',
        borderRadius: '8px'
      }}>
        <Text type="secondary" style={{ fontSize: '11px' }}>
          <strong>FDI Tooth Numbering:</strong> Q1 (18-11) | Q2 (21-28) | Q3 (31-38) | Q4 (41-48)
        </Text>
      </div>

      {/* Quick Actions */}
      <div style={{ marginTop: '24px', textAlign: 'center' }}>
        <Space wrap size="large">
          <Button type="dashed" icon={<PlusOutlined />} size="large">
            Add Treatment Plan
          </Button>
          <Button type="dashed" icon={<CameraOutlined />} size="large">
            Take Intraoral Photo
          </Button>
          <Upload
            action="/api/upload"
            onChange={handleXrayUpload}
            showUploadList={false}
            accept="image/*"
          >
            <Button type="dashed" icon={<FileImageOutlined />} size="large">
              Upload X-ray
            </Button>
          </Upload>
        </Space>
      </div>
    </div>
  );
};

export default DentalChart;